import { type ReactElement } from 'react';
import { Button, Table, TableBody, TableCell, TableHead, TableRow, Text } from '@citibox-panel/ui';
import { BatchStatusChip } from './BatchStatusChip';
import { formatCount, formatDateTime, formatPercentage } from '../lib/format';
import type { QrBatch } from '../lib/qrBatch';

export interface BatchesTableProps {
  batches: QrBatch[];
  timeZone: string | null;
  // The row whose file is on its way, so only its button shows it.
  downloadingId: string | null;
  onDownload: (id: string) => void;
}

// TODO: i18n — copy en ES hasta Fase 1.
const HEADERS = ['CREADO', 'MODELO', 'CANTIDAD', 'ESTADO', 'TERMINADO', 'EN USO', 'FICHERO'] as const;
const NONE = '—';

// Only a finished batch that kept its file offers it: a failed batch and the 2022 CBX10 ones have
// none, and a button that answers 404 is worse than no button.
const canDownload = (batch: QrBatch): boolean => batch.status === 'DONE' && batch.hasFile;

export function BatchesTable({ batches, timeZone, downloadingId, onDownload }: BatchesTableProps): ReactElement {
  return (
    <Table size="small" aria-label="Lotes de etiquetas QR">
      <TableHead>
        <TableRow>
          {HEADERS.map((header) => (
            <TableCell key={header} component="th" scope="col">
              {/* Body tone, never `secondary`: that one measures ≈2.4:1 on this surface. */}
              <Text variant="overline" component="span" bold dense>
                {header}
              </Text>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {batches.map((batch) => {
          const created = formatDateTime(batch.createdAt, timeZone);
          return (
            <TableRow key={batch.id}>
              <TableCell singleLine>
                <Text variant="body2">{created}</Text>
              </TableCell>
              <TableCell>
                <Text variant="body2">{batch.lockModel}</Text>
              </TableCell>
              <TableCell singleLine>
                <Text variant="body2">{formatCount(batch.qrAmount)}</Text>
              </TableCell>
              <TableCell>
                <BatchStatusChip status={batch.status} />
              </TableCell>
              <TableCell singleLine>
                <Text variant="body2">
                  {batch.finishedAt === null ? NONE : formatDateTime(batch.finishedAt, timeZone)}
                </Text>
              </TableCell>
              <TableCell singleLine>
                <Text variant="body2">
                  {batch.status === 'DONE'
                    ? `${formatCount(batch.inUse)} · ${formatPercentage(batch.inUsePercentage)}`
                    : NONE}
                </Text>
              </TableCell>
              <TableCell align="right">
                {canDownload(batch) && (
                  <Button
                    variant="text"
                    aria-label={`Descargar el lote del ${created}`}
                    disabled={downloadingId === batch.id}
                    loading={downloadingId === batch.id}
                    onClick={() => {
                      onDownload(batch.id);
                    }}
                  >
                    Descargar
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
